import { useEffect, useMemo, useRef, useState } from "react";
import { href } from "../lib/route";
import { calendarToday } from "../lib/days";
import { clipsByRow, DONE_CLASS, type SheetExercise } from "../lib/reviewSheet";
import { overviewHead, prNames, writtenDose } from "../lib/sessionOverview";
import { useProposals, useRosterData } from "../routes/useRosterData";
import { useReviewQueue } from "../routes/useReviewQueue";
import { useReviewSheet } from "../routes/useReviewSheet";
import { ClipPlayButton } from "./ClipPlayer";
import { Icon } from "./Icon";
import { SetNote } from "./SetNote";

/* W93 — A FINISHED WORKOUT'S OVERVIEW, OPENED FROM A DASHBOARD QUEUE ROW (his word, 2026-09-28): "when clicking in any row
 * of a client review open the workout overview thing and see at least the written and done. and have another review button
 * top or bottom right … but if we tap the review button from the dashboard we go straight in the review."
 *
 * 🔴 W118 (his word, 2026-10-01: *"it has most info left and then all the right side is empty. it could be half the
 * horizontal size … and a little more tidy man.. idk maybe containers for each exercise? do what it takes so it looks
 * amazing and not just a bunch of text"*): HALF AS WIDE (`min(92vw, 30rem)`), centred, its height following the content up
 * to 85vh with the body scrolling under a fixed head and foot. The head says who · the day, the workout, when it finished
 * and how long it has waited (`sessionOverview.overviewHead`); the body is ONE PANEL PER EXERCISE — the name with its written
 * dose, a PR as the queue's own chip, then a boxed SET · WRITTEN · DONE table; the foot is `Review ›` and Close.
 *
 * READ-ONLY, over the Dashboard (`backdropRoute` → dashboard), from the SAME sheet the review page draws (`useReviewSheet`
 * on the queue's own row), so the two can never disagree about what was written or done. Its clips play in the review's own
 * player (`ClipPlayButton` — W116's rule: where a set has numbers it has its video, never a second player) and its client
 * notes open in the review's own bubble (`SetNote`, W110's hover rule); nothing here writes. */
export function SessionOverviewWindow({ sessionId }: { sessionId: string }) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  const [today] = useState(() => calendarToday());
  const [now] = useState(() => Date.now());
  const roster = useRosterData(0, today);
  const data = roster.status === "ready" ? roster.data : null;
  const proposals = useProposals(data);
  const queue = useReviewQueue(data, proposals, today);
  const q = queue.status === "ready" ? queue.queue : null;
  const row = useMemo(() => q?.days.find((r) => r.sessionId === sessionId) ?? null, [q, sessionId]);
  const state = useReviewSheet(row);
  const sheet = state.status === "ready" ? state.sheet : null;
  const failed = roster.status === "error" ? roster.message : queue.status === "error" ? queue.message : state.status === "error" ? state.message : null;
  const outside = failed === null && q !== null && (row === null || (state.status === "ready" && sheet === null));
  const head = row === null ? null : overviewHead(row, now);
  const prs = useMemo(() => prNames(row?.flags ?? []), [row]);

  useEffect(() => {
    const el = dialogRef.current;
    if (el !== null && !el.open) el.showModal();
    heading.current?.focus();
  }, []);
  const close = () => { window.location.hash = href({ name: "dashboard" }); };

  return (
    <dialog ref={dialogRef} className="ht-window session-overview" aria-labelledby="session-overview-title"
            onCancel={(e) => { e.preventDefault(); close(); }}>
      {/* The builders' round ✕ on the window's corner (`.ht-corner-close`, W105) — the dialog's first child, beside the
          frame (which clips). */}
      <button type="button" className="ht-corner-close" aria-label="Close" title="Close" onClick={close}>
        <Icon name="x" size={18} stroke={2} />
      </button>
      <div className="ht-window-frame">
        <header className="ht-window-head session-overview-head">
          <div className="ht-window-titles">
            <p className="page-eyebrow">{head === null ? "Workout" : head.eyebrow}</p>
            <h2 className="ht-title session-overview-title" id="session-overview-title" tabIndex={-1} ref={heading}>
              {head === null ? "Workout" : head.title}
            </h2>
            {head !== null && <p className="session-overview-caption">{head.caption}</p>}
          </div>
        </header>
        <div className="ht-window-body session-overview-body">
          {failed !== null && <p className="ht-callout" role="alert">Could not load this workout: {failed}</p>}
          {outside && <p className="ht-callout">This workout is not in your review window any more.</p>}
          {failed === null && !outside && sheet === null && <p className="ht-callout loading-line" aria-live="polite">Loading the workout…</p>}
          {sheet !== null && (
            <>
              {sheet.workoutNote !== null && <p className="so-note"><span className="review-note-label">Their note</span>“{sheet.workoutNote}”</p>}
              {sheet.bands.map((b, bi) => b.kind === "folded"
                ? <p key={bi} className="session-overview-folded">{b.line}</p>
                : (
                  <section key={bi} className="so-band">
                    {/* W104: a section the coach named says its name; an unnamed one draws no label. */}
                    {b.title !== null && <h3 className="so-band-title">{b.title}</h3>}
                    {b.items.map((it, ii) => it.kind === "single"
                      ? <OverviewExercise key={ii} ex={it.exercise} pr={prs.has(it.exercise.name)} />
                      : (
                        <div key={ii} className="so-superset">
                          <p className="so-superset-label"><Icon name="link" size={12} />Superset</p>
                          {it.exercises.map((ex) => <OverviewExercise key={ex.id} ex={ex} pr={prs.has(ex.name)} />)}
                        </div>
                      ))}
                  </section>
                ))}
            </>
          )}
        </div>
        <footer className="ht-window-foot session-overview-foot">
          <button type="button" className="ht-button" onClick={close}>Close</button>
          <a className="ht-button ht-button-primary session-overview-review" href={href({ name: "review", sessionId })}>Review ›</a>
        </footer>
      </div>
    </dialog>
  );
}

/** One exercise's panel: its name and written dose (a PR as the queue's chip), then its sets — written beside done, a clip
 *  behind the review's play button in its set's row, the client's note behind its icon. */
function OverviewExercise({ ex, pr }: { ex: SheetExercise; pr: boolean }) {
  const clips = clipsByRow(ex.clips);
  const dose = writtenDose(ex.sets);
  return (
    <article className="so-panel">
      <header className="so-panel-head">
        <h4 className="so-name">{ex.name}</h4>
        {dose !== null && <span className="so-dose">{dose}</span>}
        {pr && <span className="queue-chip is-ok">PR</span>}
      </header>
      <table className="so-sets">
        <thead><tr><th scope="col">Set</th><th scope="col">Written</th><th scope="col">Done</th></tr></thead>
        <tbody>
          {ex.sets.map((s) => (
            <tr key={s.label} className={s.state === "ramp" ? "so-set is-ramp" : "so-set"}>
              <td className="so-set-n">{s.label}</td>
              <td className="review-written">{s.written}</td>
              <td className={DONE_CLASS[s.state]}>
                <span className="review-done-line">
                  <span>{s.done}</span>
                  <ClipPlayButton clips={clips.byRow.get(s.label) ?? []} setWords={`${ex.name}, set ${s.label}`} setLabel={s.label} />
                  {s.note !== null && <SetNote note={s.note} setWords={`${ex.name}, set ${s.label}`} />}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {clips.loose.length > 0 && <div className="review-clips"><ClipPlayButton clips={clips.loose} setWords={ex.name} setLabel={null} /></div>}
    </article>
  );
}
